/**
 * The idle clock: thirty minutes without activity signs the person out,
 * and the last two of those minutes are a warning. One timestamp in
 * localStorage is the record of the last activity, so every open tab
 * reads the same clock and activity in any tab counts for all of them.
 * The arithmetic lives here, pure, so the component that watches events
 * and renders the warning stays small.
 */

export const IDLE_LIMIT_MS = 30 * 60_000;
export const IDLE_WARNING_MS = 2 * 60_000;
export const ACTIVITY_KEY = 'olive:last-activity';

export type IdleState =
  | { kind: 'active' }
  | { kind: 'warning'; remainingMs: number }
  | { kind: 'expired' };

export function idleState(lastActivityAt: number, now: number): IdleState {
  const elapsed = now - lastActivityAt;
  if (elapsed >= IDLE_LIMIT_MS) return { kind: 'expired' };
  if (elapsed >= IDLE_LIMIT_MS - IDLE_WARNING_MS) {
    return { kind: 'warning', remainingMs: IDLE_LIMIT_MS - elapsed };
  }
  return { kind: 'active' };
}

/** The shared stamp, or null when it is missing, unreadable or garbled. */
export function readLastActivity(storage: Storage | null): number | null {
  if (storage === null) return null;
  try {
    const raw = storage.getItem(ACTIVITY_KEY);
    if (raw === null || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

/** Storage can refuse (a private window, blocked site data); activity
 *  tracking then falls back to this tab alone. */
export function writeLastActivity(storage: Storage | null, now: number): void {
  if (storage === null) return;
  try {
    storage.setItem(ACTIVITY_KEY, String(now));
  } catch {
    /* this tab keeps its own clock */
  }
}

/** The countdown as m:ss. */
export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
